import { MoveList } from 'chessboard/model/move-list/types';
import { isEPCapture } from 'chessboard/model/move/is-ep-capture';
import { MoveListShared } from 'components/move-list-shared/config/types';
import type { MoveListComponent } from 'components/move-list/config/types';
import { Game } from 'chessboard/model/game/types';
import type { GameAPI } from 'chessboard/types';
import type { GameMetadata } from '../store/types';

export type TooltipProps = {
  tooltipMessage: string;
  handleClick?: (event: MouseEvent) => void;
  iconClassName: string;
};

/**
 * determine props to pass to Tooltip component for a node
 */
export function getTooltipProps({
  node,
  translations,
  gameMetadata,
  game,
}: {
  node: MoveListComponent.NodeModel | undefined;
  translations: MoveList.Translations;
  gameMetadata?: GameMetadata;
  game?: GameAPI;
}): TooltipProps | null {
  if (!node) {
    return null;
  }

  // todo: variations https://chesscom.atlassian.net/browse/CV-347760
  if (node.ply && gameMetadata?.threefoldIndexes?.includes(node.ply - 1)) {
    return {
      tooltipMessage: translations[MoveList.Translation.Tooltips.ThreeFoldRepetition](),
      iconClassName: MoveListShared.Classes.ThreeFoldIcon,
    };
  }

  if (node.flags && isEPCapture(node.flags)) {
    return {
      tooltipMessage: translations[MoveList.Translation.Tooltips.EnPassant](),
      handleClick: (event) => {
        const enPassantInfoURL = game?.getOptions().assetPaths?.enPassant;
        if (!enPassantInfoURL) {
          return;
        }
        event.stopPropagation();
        window.open(enPassantInfoURL, '_blank');
      },
      iconClassName: MoveListShared.Classes.CircleInfo,
    };
  }

  return null;
}

/**
 * determine props to pass to Tooltip component for a result node
 */
export function getResultTooltipProps({
  translations,
  gameMetadata,
}: {
  translations: MoveList.Translations;
  gameMetadata?: GameMetadata;
}): TooltipProps | null {
  const resultPositionInfo = gameMetadata?.resultPositionInfo;
  if (!resultPositionInfo) {
    return null;
  }

  if (resultPositionInfo.fiftyMoveRule) {
    return {
      tooltipMessage: translations[MoveList.Translation.Tooltips.FiftyMove](),
      iconClassName: MoveListShared.Classes.CircleInfo,
    };
  }

  if (resultPositionInfo.insufficient) {
    return {
      tooltipMessage: translations[MoveList.Translation.Tooltips.InsufficientMaterial](),
      iconClassName: MoveListShared.Classes.CircleInfo,
    };
  }

  if (resultPositionInfo.timeoutVsInsufficientMaterial && resultPositionInfo.lastMoveColor) {
    const tooltipMessage =
      resultPositionInfo.lastMoveColor === Game.ColorsAsNumbers.White
        ? translations[MoveList.Translation.Tooltips.BlackTimeoutVsInsufficientMaterial]()
        : translations[MoveList.Translation.Tooltips.WhiteTimeoutVsInsufficientMaterial]();

    return {
      tooltipMessage,
      iconClassName: MoveListShared.Classes.CircleInfo,
    };
  }

  if (resultPositionInfo.drawByAgreement) {
    return {
      tooltipMessage: translations[MoveList.Translation.Tooltips.DrawByAgreement](),
      iconClassName: MoveListShared.Classes.CircleInfo,
    };
  }

  if (resultPositionInfo.stalemate) {
    return {
      tooltipMessage: translations[MoveList.Translation.Tooltips.Stalemate](),
      iconClassName: MoveListShared.Classes.CircleInfo,
    };
  }

  return null;
}
